<template>
    <transition-group name="toast" tag="div" class="notification-toast-container">
        <div
            v-for="toast in activeToasts"
            :key="toast.id"
            class="notification-toast"
            @click="handleToastClick(toast)"
            @mouseenter="pauseToastTimeout(toast.id)"
            @mouseleave="resumeToastTimeout(toast.id)"
        >
            <div class="toast-content">
                <div class="toast-icon">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="feather feather-bell"
                    >
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                        <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                    </svg>
                </div>
                <div class="toast-body">
                    <div class="toast-title">{{ toast.title }}</div>
                    <div class="toast-message">{{ toast.message }}</div>
                </div>
                <button
                    type="button"
                    class="toast-close"
                    @click.stop="dismissToast(toast.id)"
                    aria-label="Fechar"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
            </div>
        </div>
    </transition-group>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useNotificacoesStore } from '@/stores/notificacoes';
import { useRouter } from 'vue-router';

const notificacoesStore = useNotificacoesStore();
const router = useRouter();

const activeToasts = ref([]);
const toastTimeouts = new Map();
const toastPauseTimes = new Map(); // Armazena quando o toast foi pausado (timestamp)
const TOAST_DURATION = 5000; // 5 segundos

/**
 * Adiciona um novo toast quando uma notificação chega
 */
const addToast = (notification) => {
    // Remove toast duplicado se existir (evita múltiplos toasts da mesma notificação)
    const existingIndex = activeToasts.value.findIndex(t => t.notificationId === notification.id);
    if (existingIndex !== -1) {
        dismissToast(activeToasts.value[existingIndex].id);
    }

    const toastId = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // O backend pode enviar title/message/action_url tanto no nível superior quanto em data
    // Vamos buscar em ambos os lugares
    const title = notification.title || notification.data?.title || 'Nova notificação';
    const message = notification.message || notification.data?.message || 'Você recebeu uma nova notificação';
    const actionUrl = notification.action_url || notification.data?.action_url || null;
    
    console.log('📝 Toast criado:', { title, message, actionUrl, notification });
    
    const toast = {
        id: toastId,
        notificationId: notification.id,
        title: title,
        message: message,
        actionUrl: actionUrl,
        notification: notification,
        createdAt: Date.now() // Timestamp de criação para controle de timeout
    };

    activeToasts.value.push(toast);

    // Inicia o timeout para remover após 5 segundos
    startToastTimeout(toastId);
};

/**
 * Inicia o timeout para remover o toast
 */
const startToastTimeout = (toastId, remainingTime = TOAST_DURATION) => {
    // Limpa timeout anterior se existir
    const existingTimeout = toastTimeouts.get(toastId);
    if (existingTimeout) {
        clearTimeout(existingTimeout);
    }

    // Garante que o tempo restante seja válido
    if (remainingTime <= 0) {
        dismissToast(toastId);
        return;
    }

    const timeout = setTimeout(() => {
        dismissToast(toastId);
    }, remainingTime);

    toastTimeouts.set(toastId, timeout);
};

/**
 * Pausa o timeout do toast quando o mouse entra
 */
const pauseToastTimeout = (toastId) => {
    const timeout = toastTimeouts.get(toastId);
    if (!timeout) return;

    // Calcula o tempo restante baseado no timestamp de criação
    const toast = activeToasts.value.find(t => t.id === toastId);
    if (!toast) return;

    const elapsed = Date.now() - toast.createdAt;
    let remaining = TOAST_DURATION - elapsed;

    // Se já tiver sido pausado antes, usa o tempo restante salvo
    const previousPause = toastPauseTimes.get(toastId);
    if (previousPause) {
        // Calcula quanto tempo passou desde a pausa anterior
        const timeSincePause = Date.now() - previousPause.pausedAt;
        remaining = previousPause.remaining - timeSincePause;
    }

    // Se ainda houver tempo restante, pausa
    if (remaining > 0) {
        clearTimeout(timeout);
        toastTimeouts.delete(toastId);
        toastPauseTimes.set(toastId, { pausedAt: Date.now(), remaining: remaining });
    } else {
        // Se o tempo já acabou, apenas remove
        dismissToast(toastId);
    }
};

/**
 * Retoma o timeout do toast quando o mouse sai
 */
const resumeToastTimeout = (toastId) => {
    const pauseInfo = toastPauseTimes.get(toastId);
    if (!pauseInfo) {
        // Se não tinha informação de pausa, recalcula baseado no tempo total
        const toast = activeToasts.value.find(t => t.id === toastId);
        if (!toast) return;
        
        const elapsed = Date.now() - toast.createdAt;
        const remaining = TOAST_DURATION - elapsed;
        if (remaining > 0) {
            startToastTimeout(toastId, remaining);
        }
        return;
    }

    // Retoma com o tempo restante salvo
    startToastTimeout(toastId, pauseInfo.remaining);
    toastPauseTimes.delete(toastId);
};

/**
 * Remove um toast
 */
const dismissToast = (toastId) => {
    const index = activeToasts.value.findIndex(t => t.id === toastId);
    if (index !== -1) {
        // Limpa o timeout se existir
        const timeout = toastTimeouts.get(toastId);
        if (timeout) {
            clearTimeout(timeout);
            toastTimeouts.delete(toastId);
        }
        
        // Limpa informações de pausa
        toastPauseTimes.delete(toastId);
        
        activeToasts.value.splice(index, 1);
    }
};

/**
 * Handler para quando o toast é clicado
 */
const handleToastClick = (toast) => {
    // Marca como lida se necessário
    if (!toast.notification.read_at) {
        notificacoesStore.markAsRead(toast.notificationId);
    }

    // Navega para a URL da ação se existir
    if (toast.actionUrl) {
        try {
            const url = new URL(toast.actionUrl);
            window.open(url.href, '_blank', 'noopener,noreferrer');
        } catch (e) {
            const fullUrl = window.location.origin + toast.actionUrl;
            window.open(fullUrl, '_blank', 'noopener,noreferrer');
        }
    }

    // Remove o toast
    dismissToast(toast.id);
};

/**
 * Handler para evento de nova notificação recebida
 */
const handleNewNotification = (event) => {
    const notification = event.detail;
    if (notification && !notification.read_at) {
        console.log('📢 Exibindo toast para nova notificação:', notification);
        addToast(notification);
    }
};

onMounted(() => {
    // Escuta eventos customizados de novas notificações
    window.addEventListener('new-notification-received', handleNewNotification);
});

onUnmounted(() => {
    // Remove o listener de eventos
    window.removeEventListener('new-notification-received', handleNewNotification);
    
    // Limpa todos os timeouts pendentes
    toastTimeouts.forEach(timeout => clearTimeout(timeout));
    toastTimeouts.clear();
    
    // Limpa informações de pausa
    toastPauseTimes.clear();
});
</script>

<style scoped>
.notification-toast-container {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 12px;
    pointer-events: none;
    max-width: 400px;
}

.notification-toast {
    pointer-events: all;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
    cursor: pointer;
    transition: all 0.3s ease;
    overflow: hidden;
    border-left: 4px solid #0d6efd;
}

.notification-toast:hover {
    transform: translateX(-4px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.05);
    border-left-width: 5px;
}

.toast-content {
    display: flex;
    align-items: flex-start;
    padding: 16px;
    gap: 12px;
}

.toast-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #0d6efd 0%, #0056b3 100%);
    border-radius: 8px;
    color: #fff;
}

.toast-body {
    flex: 1;
    min-width: 0;
}

.toast-title {
    font-weight: 600;
    font-size: 14px;
    color: #212529;
    margin-bottom: 4px;
    line-height: 1.4;
}

.toast-message {
    font-size: 13px;
    color: #6c757d;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.toast-close {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    color: #6c757d;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s ease;
    padding: 0;
}

.toast-close:hover {
    background: #f8f9fa;
    color: #212529;
}

/* Animações de entrada e saída */
.toast-enter-active {
    animation: slideInRight 0.3s ease-out;
}

.toast-leave-active {
    animation: slideOutRight 0.3s ease-in;
}

@keyframes slideInRight {
    from {
        opacity: 0;
        transform: translateX(100%);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes slideOutRight {
    from {
        opacity: 1;
        transform: translateX(0);
    }
    to {
        opacity: 0;
        transform: translateX(100%);
    }
}

/* Modo escuro */
[data-theme="dark"] .notification-toast {
    background: #1f2937;
    border-left-color: #3b82f6;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1);
}

[data-theme="dark"] .notification-toast:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.15);
}

[data-theme="dark"] .toast-title {
    color: #f9fafb;
}

[data-theme="dark"] .toast-message {
    color: #9ca3af;
}

[data-theme="dark"] .toast-close {
    color: #9ca3af;
}

[data-theme="dark"] .toast-close:hover {
    background: #374151;
    color: #f9fafb;
}
</style>

